/**
 * COME_OUT (craps) — shooter rolls for the come-out. Outcome resolves
 * to a natural (7/11, terminal win), craps (2/3/12, terminal loss),
 * or a point established (4/5/6/8/9/10, turn continues).
 *
 * Server transitions on dice_result:
 *   - terminal → SHOOTER_SET_STACK directly (collapsed flow)
 *   - point set → POINT_ESTABLISHED
 *
 * onExit runs pure resolution choreography. If the next state's
 * shooter differs from the one who just rolled, runShooterHandoff
 * fires (terminal → SHOOTER_SET_STACK with new shooter). On a point
 * established, the same shooter stays — no handoff. Dice are NOT
 * cleared here:
 *   - terminal: SHOOTER_SET_STACK.onEnter calls enterBettingMode
 *     which clears dice as part of the betting transition.
 *   - point established: dice persist on the table for the shooter
 *     to pick back up at POINT_ROLL (CLAUDE.md rule #5).
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class ComeOutState extends State {
  private _enterShooterId: string | null = null;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const shooterId = update.currentPlayerId ?? update.bankerId;

    this._enterShooterId = shooterId ?? null;
    this._resetRollSetup();

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();
    game.instructionBanner?.hide();

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this._updateProfiles(update);
    await this._checkPlayers(update);

    this._showOpponentReadyDice(shooterId, 2);

    // Roll authorization is STATE (update.rollAuthorizedFor). The server
    // authorizes the shooter just AFTER the transition broadcast, so the
    // authorization usually rides the immediate follow-up update (→ onUpdate);
    // _maybeSetupLocalRoll fires from whichever hook first sees us authorized,
    // exactly once. Resume-safe — on state:full the field rides the enter
    // broadcast and this fires here.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    this._updateProfiles(update);
    await this._checkPlayers(update);
    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    await this._checkPlayers(update);
    await this.game.playRollResolution(update);

    const next = update.currentPlayerId ?? update.bankerId ?? null;
    const prev = this._enterShooterId;
    if (prev && next && prev !== next) {
      await this.game.runShooterHandoff(update, prev, next);
    }

    // Spawn next round's banker zone before the camera lerps — see
    // PointRollState.onExit for the rationale.
    this.game.primeBetZones();
  }
}
